/**
 * Schema Zod para AuthSession — gate de localStorage rehydrate (D-20).
 *
 * Usado por `src/contexts/AuthContext.tsx` para descartar sessões corrompidas
 * ou adulteradas antes que `setSession(session)` propague shape inválido para
 * downstream (queries, hooks, RLS-bound code paths).
 *
 * Nota: este schema NÃO valida assinatura criptográfica do JWT — isso é
 * responsabilidade do Supabase Auth no próximo request. O escopo aqui é
 * fechar o vetor "shape corrompido em localStorage" (XSS, extensão maliciosa,
 * race condition de logout). Ver threat register T-07-01..T-07-04.
 *
 * Não usamos `.strict()` porque a sessão Supabase pode incluir campos extras
 * (ex.: `provider_token`, `provider_refresh_token` em fluxos OAuth) que não
 * exigimos mas também não devemos rejeitar. Comportamento default do Zod
 * é passthrough para chaves desconhecidas — correto para este caso.
 */
import { z } from 'zod';

// ============================================================================
// AuthSession Schema
// ============================================================================

// UUID-shaped string: 8-4-4-4-12 hex chars, any variant/version.
// z.string().uuid() uses RFC 4122 strict variant bits which rejects local
// Supabase dev seed UUIDs (e.g. 11111111-1111-1111-1111-111111111111).
// The goal here is shape validation (not a UUID = corrupted), not RFC compliance.
const uuidLike = z.string().regex(
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  'Invalid UUID format',
);

export const AuthSessionSchema = z.object({
  access_token: z.string().min(1),
  refresh_token: z.string().min(1),
  expires_at: z.number().int().nonnegative(),
  expires_in: z.number().int().nonnegative(),
  token_type: z.literal('bearer'),
  user: z.object({
    id: uuidLike,
    email: z.string().email(),
  }),
});

export type AuthSession = z.infer<typeof AuthSessionSchema>;
